/**
 * Lets the API client tell the automatic reporter about a failed call without
 * importing it (the reporter posts through `fetch`, not through the client).
 *
 * A call that failed because the server did (5xx, or a reply that was not the
 * API at all) or because a connection that claims to be up gave no answer.
 */

export type ApiFailure =
  | {
      readonly kind: 'server';
      readonly status: number;
      readonly path: string;
      readonly code: string;
      readonly diagnostic: string;
      /** True when the API's own error handler answered, so it already filed its own report. */
      readonly answered: boolean;
      /** The token the API sent back with the failed reply (X-Support-Followup), if it did. */
      readonly followup?: string | undefined;
    }
  | { readonly kind: 'network'; readonly path: string };

let handler: ((failure: ApiFailure) => void) | null = null;

export function onApiFailure(next: ((failure: ApiFailure) => void) | null): void {
  handler = next;
}

/** Never throws: reporting a failure must not become a second one. */
export function notifyApiFailure(failure: ApiFailure): void {
  try {
    handler?.(failure);
  } catch {
    // Nothing to do. The call this describes is already failing for its caller.
  }
}
